import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchAllAssessments,
  fetchAssessment,
  createAssessment,
  updateAssessment,
  updateAssessmentFormSchema,
  deleteAssessment,
  fetchAssessmentLinks,
  fetchOrganizationAssessmentLinks,
  AssessmentLink,
  fetchAssessedSessions,
  fetchAssessedSession,
  AssessedSession,
  fetchAssessmentHistory,
} from "../actions/assessmentActions";
import type { FormOutputSchema } from "../types/formTypes";

// Types for generated content
export interface GeneratedQuestionCriteria {
  analyze_question: string;
  guidance: string;
  context: string;
  id: string;
  answer_type?: string;
  answer_guidance?: string;
}

export interface GeneratedQuestion {
  id: string;
  question_type: string;
  required: boolean;
  initial_question: string;
  topic: string;
  criteria: GeneratedQuestionCriteria[];
  min_num_turns: number;
  max_num_turns: number;
  min_criteria_met: number;
}

export interface InterviewConfiguration {
  min_num_turns: number;
  max_num_turns: number;
  min_num_questions_answered: number;
  non_job: boolean;
  positive_feedback: boolean;
  reflective_responses: boolean;
}

// Post-interview step interface - matches backend NextSteps model
export interface PostInterviewStep {
  min_score: number;
  outro_text: string;
  tone?: string;
  share_summary?: boolean;
  ask_questions_at_end?: boolean;
  will_end_chat?: boolean;
  qa_context?: string;
  qa_next_item_on_agenda?: string;
  qa_additional_topics?: string[];
  qa_post?: string; // This is a string in the backend, not boolean
}

// Assessment interface
export interface Assessment {
  id: string;
  resource_key: string | null;
  organization_id: string;
  creator_user_id: string;
  environment: string;
  creation_time: string;
  modified_time: string;

  // Display title for admin UI search/display
  display_title?: string | null;

  // Category for the assessment
  category?: string | null;

  scenario_description: string;
  scenario_category: string;
  scenario_applicant: string;
  context: string;
  expected_time_minutes: number;
  time_limit_minutes: number;
  discussion_topic: string;
  scenario_goal?: string | null;
  scenario_evaluator?: string | null;
  no_include?: string | null;

  // Q&A topics that can be discussed during the interview
  allowed_qa_topics?: string[] | null;
  ask_for_questions_before_start?: boolean;

  // Generation status - always present
  generation_status: "init" | "pending" | "done";

  // Processing type - determines whether this is a memory or form assessment
  assessment_processing_type: "memory" | "form";

  // Generated fields - only populated when status is "done"
  generated_questions?: GeneratedQuestion[] | null;
  evaluation_criteria?: string[] | null;
  interview_configuration?: InterviewConfiguration | null;
  payload?: any | null; // Full generated payload
  form_output_schema?: FormOutputSchema | null; // Form schema for form-based assessments

  // Active status - indicates if this is the latest version
  is_active?: boolean;
  check_for_suspicious?: boolean;
  check_for_seeking_clarification?: boolean;
  max_num_user_queries_per_question?: number;
  max_num_poorly_answered_questions?: number;
  min_words_for_chat?: number;
  max_words_for_chat?: number;

  // Additional guidance for the interviewer
  additional_guidance?: string[] | null;
  
  // Timeout settings
  post_insight_timeout_seconds?: number;
  post_statement_timeout_seconds?: number;

  // Post-interview steps (outro configuration)
  post_interview_steps?: PostInterviewStep[] | null;

  // Guidance for interviewer on how to inject their own thoughts
  without_guidance?: string;

  // Controversial content detection configuration
  controversial_content_config?: ControversialContentConfig | null;

  // Control graph for behavior-based interviews
  control_graph?: any | null;
}

// Controversial content types
export interface ControversialCategory {
  title: string;
  associated_terms: string[];
  description: string;
  example: string;
}

export interface ControversialContentConfig {
  controversial_categories: ControversialCategory[];
  classification_context: string;
  require_confirmation: boolean;
  cooldown_timer: number;
  response_mode: "no_change" | "non_committal";
}

// State interface
interface AssessmentState {
  assessments: Assessment[];
  currentAssessment: Assessment | null;
  assessmentHistory: Assessment[];
  assessmentLinks: AssessmentLink[];
  organizationAssessmentLinks: AssessmentLink[];
  assessedSessions: AssessedSession[];
  currentAssessedSession: AssessedSession | null;
  loading: boolean;
  error: string | null;
  linksLoading: boolean;
  linksError: string | null;
  sessionsLoading: boolean;
  sessionsError: string | null;
  historyLoading: boolean;
  historyError: string | null;
}
// Initial state
const initialState: AssessmentState = {
  assessments: [],
  currentAssessment: null,
  assessmentHistory: [],
  assessmentLinks: [],
  organizationAssessmentLinks: [],
  assessedSessions: [],
  currentAssessedSession: null,
  loading: false,
  error: null,
  linksLoading: false,
  linksError: null,
  sessionsLoading: false,
  sessionsError: null,
  historyLoading: false,
  historyError: null,
};

// Create the slice
const assessmentSlice = createSlice({
  name: "assessments",
  initialState,
  reducers: {
    clearCurrentAssessment(state) {
      state.currentAssessment = null;
    },
    clearCurrentAssessedSession(state) {
      state.currentAssessedSession = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<AssessmentState>) => {
    // Handle fetchAllAssessments
    builder
      .addCase(fetchAllAssessments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllAssessments.fulfilled,
        (state, action: PayloadAction<Assessment[]>) => {
          state.loading = false;
          state.assessments = action.payload;
        }
      )
      .addCase(fetchAllAssessments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assessments";
      });

    // Handle fetchAssessment
    builder
      .addCase(fetchAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAssessment.fulfilled,
        (state, action: PayloadAction<Assessment>) => {
          state.loading = false;
          state.currentAssessment = action.payload;
        }
      )
      .addCase(fetchAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assessment";
      });

    // Handle createAssessment
    builder
      .addCase(createAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createAssessment.fulfilled,
        (state, action: PayloadAction<Assessment>) => {
          state.loading = false;
          state.currentAssessment = action.payload;
          // Add the new assessment to the list
          state.assessments.unshift(action.payload);
        }
      )
      .addCase(createAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create assessment";
      });

    // Handle updateAssessment
    builder
      .addCase(updateAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateAssessment.fulfilled,
        (state, action: PayloadAction<Assessment>) => {
          state.loading = false;
          // Always update currentAssessment with the new assessment data
          // This ensures the new version is immediately available in Redux
          state.currentAssessment = action.payload;
          
          // Update the assessment in the list
          const index = state.assessments.findIndex(
            (assessment) => assessment.id === action.payload.id
          );
          if (index !== -1) {
            state.assessments[index] = action.payload;
          } else {
            // If it's a new assessment (different ID), add it to the list
            state.assessments.unshift(action.payload);
          }
        }
      )
      .addCase(updateAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update assessment";
      });

    // Handle updateAssessmentFormSchema
    builder
      .addCase(updateAssessmentFormSchema.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateAssessmentFormSchema.fulfilled,
        (state, action: PayloadAction<Assessment>) => {
          state.loading = false;
          // Always update currentAssessment with the new assessment data
          // This ensures the new version is immediately available in Redux
          state.currentAssessment = action.payload;

          // Update the assessment in the list
          const index = state.assessments.findIndex(
            (assessment) => assessment.id === action.payload.id
          );
          if (index !== -1) {
            state.assessments[index] = action.payload;
          } else {
            // If it's a new assessment (different ID), add it to the list
            state.assessments.unshift(action.payload);
          }
        }
      )
      .addCase(updateAssessmentFormSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update form schema";
      });

    // Handle deleteAssessment
    builder
      .addCase(deleteAssessment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        deleteAssessment.fulfilled,
        (state, action: PayloadAction<{ id: string }>) => {
          state.loading = false;
          // Remove the assessment from the list
          state.assessments = state.assessments.filter(
            (assessment) => assessment.id !== action.payload.id
          );
          // Clear current assessment if it was deleted
          if (state.currentAssessment?.id === action.payload.id) {
            state.currentAssessment = null;
          }
        }
      )
      .addCase(deleteAssessment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete assessment";
      });

    // Handle fetchAssessmentLinks
    builder
      .addCase(fetchAssessmentLinks.pending, (state) => {
        state.linksLoading = true;
        state.linksError = null;
      })
      .addCase(
        fetchAssessmentLinks.fulfilled,
        (state, action: PayloadAction<AssessmentLink[]>) => {
          state.linksLoading = false;
          state.assessmentLinks = action.payload;
        }
      )
      .addCase(fetchAssessmentLinks.rejected, (state, action) => {
        state.linksLoading = false;
        state.linksError =
          action.error.message || "Failed to fetch assessment links";
      });

    // Handle fetchOrganizationAssessmentLinks
    builder
      .addCase(fetchOrganizationAssessmentLinks.pending, (state) => {
        state.linksLoading = true;
        state.linksError = null;
      })
      .addCase(
        fetchOrganizationAssessmentLinks.fulfilled,
        (state, action: PayloadAction<AssessmentLink[]>) => {
          state.linksLoading = false;
          state.organizationAssessmentLinks = action.payload;
        }
      )
      .addCase(fetchOrganizationAssessmentLinks.rejected, (state, action) => {
        state.linksLoading = false;
        state.linksError =
          action.error.message ||
          "Failed to fetch organization assessment links";
      });

    // Handle fetchAssessedSessions
    builder
      .addCase(fetchAssessedSessions.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(
        fetchAssessedSessions.fulfilled,
        (state, action: PayloadAction<AssessedSession[]>) => {
          state.sessionsLoading = false;
          state.assessedSessions = action.payload;
        }
      )
      .addCase(fetchAssessedSessions.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError =
          action.error.message || "Failed to fetch assessed sessions";
      });

    // Handle fetchAssessedSession
    builder
      .addCase(fetchAssessedSession.pending, (state) => {
        state.sessionsLoading = true;
        state.sessionsError = null;
      })
      .addCase(
        fetchAssessedSession.fulfilled,
        (state, action: PayloadAction<AssessedSession>) => {
          state.sessionsLoading = false;
          state.currentAssessedSession = action.payload;
        }
      )
      .addCase(fetchAssessedSession.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.sessionsError =
          action.error.message || "Failed to fetch assessed session";
      });

    // Handle fetchAssessmentHistory
    builder
      .addCase(fetchAssessmentHistory.pending, (state) => {
        state.historyLoading = true;
        state.historyError = null;
      })
      .addCase(
        fetchAssessmentHistory.fulfilled,
        (state, action: PayloadAction<Assessment[]>) => {
          state.historyLoading = false;
          state.assessmentHistory = action.payload;
        }
      )
      .addCase(fetchAssessmentHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.historyError =
          action.error.message || "Failed to fetch assessment history";
      });

  },
});

export const { clearCurrentAssessment, clearCurrentAssessedSession } =
  assessmentSlice.actions;
export default assessmentSlice.reducer;
